import { mapFromNumber } from './arrays';

/**
 * Generate a random integer within a min / max range.
 * @param minArg
 * @param maxArg
 */
export const getRandomInt = (minArg: number, maxArg: number): number => {
  const min = Math.ceil(minArg);
  const max = Math.floor(maxArg);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Given the length of an array get a random integer index.
 * @param length
 */
export const getRandomIndex = (length: number): number =>
  getRandomInt(0, length - 1);

/**
 * Given an iterable (string, or array) - return a random value from
 * that iterable.
 * @param iterable
 */
export const getRandomFromIter = <I>(iterable: Array<I> | string): I | string =>
  iterable[getRandomIndex(iterable.length)];

/**
 * Generate a random iterable given a base iterable from which random
 * values are selected.  Returns a function that takes, min / max size
 * values and returns the random iterable.
 * @param baseIter
 */
export const generateRandomIterable =
  <I>(baseIter: I[] | string) =>
  (minSize: number, maxSize: number): Array<I> | string[] =>
    mapFromNumber(getRandomInt(minSize, maxSize), () =>
      getRandomFromIter(baseIter),
    );

/**
 * Generates a random array of strings from those used
 * in hexadecimal values.
 */
export const getRandomHexadecimalArray =
  generateRandomIterable('0123456789abcdef');

/**
 * Generate a random hexadecimal string.
 */
export const getRandomHexadecimal = (): string =>
  getRandomHexadecimalArray(2, 2).join('');

export const getRandomLetterArray = generateRandomIterable(
  'abcdefghijklmnopqrstuvwxyz',
);

export const getRandomLetters = (minSize: number, maxSize: number) =>
  getRandomLetterArray(minSize, maxSize).join('');

/**
 * Generate a random hexademical color string. #rrggbb
 */
export const getRandomColor = (): string =>
  `#${getRandomHexadecimal()}${getRandomHexadecimal()}${getRandomHexadecimal()}`;

interface GetRandomText {
  minWordCount: number;
  maxWordCount: number;
  minWordLength: number;
  maxWordLength: number;
}
export const getRandomText = ({
  minWordCount,
  maxWordCount,
  minWordLength,
  maxWordLength,
}: GetRandomText) =>
  mapFromNumber(getRandomInt(minWordCount, maxWordCount), () =>
    getRandomLetters(minWordLength, maxWordLength),
  ).join(' ');

export const generateRandomArray = () =>
  [...Array(getRandomInt(10, 100)).keys()].map(() => getRandomInt(10, 100));
